// This component is used to display the CMP button on the account page.

// Imports ------------------------------------------------------------------------------------------------------------

import Button from '@components/atoms/buttons/confirmButton';
import CmpUtils from '@shared/utils/cmp';
import Container from '@components/atoms/container/accountCmpContainer';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import Item from './accountItem';
import ScreenUtils from '@screen';
import Spinner from '../spinner/spinner';
import TranslationManager from '@shared/assets/locale/translationsManager';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { useAppSelector } from '@shared/store/hooks';
import { useCallback, useMemo, useState } from 'react';
import { focusSelector } from '@shared/store/slices/focus.slice';

// Constants ----------------------------------------------------------------------------------------------------------

const LITERALS = 'PAGES.SETTINGS.ACCOUNT';

/**
 * Account CMP button component for the account page.
 * Handles the CMP button display.
 *
 * @returns {JSX.Element} Account CMP button container
 */
export default function AccountCmpButton() {
  const { display, props, text } = useAccountCmpButton();

  // Render only if CMP is enabled
  if (!display.cmpButton) return null;

  // Render the account CMP button
  return (
    <Container>
      <Item {...props.item} />
      <Button {...props.button}>{text.configure}</Button>
      {display.spinner ? <Spinner {...props.spinner} /> : null}
    </Container>
  );
}

/**
 * Hook to use the account CMP button.
 * Handles the account CMP button logic and returns the props for the account CMP button.
 *
 * @returns {Object} Object with the props for the account CMP button
 */
function useAccountCmpButton() {
  // State and selector hooks

  const { focus } = useAppSelector(focusSelector);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [displaySpinner, setDisplaySpinner] = useState(false);

  // Callbacks

  const openCmp = useCallback(() => {
    if (FlavourConfig.capabilities.cmp) {
      setDisplaySpinner(true);
      CmpUtils.openCmp(() => setDisplaySpinner(false));
    }
  }, []);

  // Keyboard hook

  useKeyboard({
    focus: Focus.ACCOUNT,
    keyDownHandler: (key) => {
      switch (key) {
        case 'SELECT':
          openCmp();
          break;
        default:
          break;
      }
    },
  });

  // Memoized values

  const { display, props, text } = useMemo(() => {
    const menuFocused = focus === Focus.MENU;
    const text = {
      configure: TranslationManager.get(`${LITERALS}.DATA.CONFIGURE`),
      privacyPolicy: TranslationManager.get(`${LITERALS}.DATA.PRIVACY_POLICY`),
    };
    return {
      display: {
        cmpButton: FlavourConfig.capabilities.cmp,
        spinner: displaySpinner,
      },
      props: {
        button: {
          focused: !menuFocused || buttonHovered,
          fontSize: 36,
          height: 80,
          margin: `0 0 0 ${20 * ScreenUtils.widthRatio}px`,
          width: 300,
          onClick: openCmp,
          onPointerEnter: () => setButtonHovered(true),
          onPointerLeave: () => setButtonHovered(false),
        },
        item: {
          displayTitleOnly: true,
          title: text.privacyPolicy,
        },
        spinner: {
          type: 'account',
        },
      },
      text,
    };
  }, [buttonHovered, displaySpinner, focus, openCmp]);

  // Return values

  return {
    display,
    props,
    text,
  };
}
